import { Link } from 'react-router-dom';
import JsonLd from '../components/JsonLd';
import IgnisIcon from '../components/icons/IgnisIcon';
import { useDocumentMeta } from '../hooks/useDocumentMeta';

// Public B2G hub. Names the offerings, the legal duties they support and how
// access works. No prices here (pricing lives in docs/modules/municipios/ and is
// quoted only in a proposal). Art. 43: the official danger class is IPMA/ICNF's;
// Tutela supports duties, never fulfils the classification.

const SITE = 'https://tutela.land';

type Offer = {
  name: string;
  status: 'live' | 'pilot' | 'planned';
  who: string;
  what: string;
  duty: string;
  to?: string;
};

const OFFERS: Offer[] = [
  {
    name: 'Briefing diário por concelho',
    status: 'live',
    who: 'GTF e SMPC',
    what: 'Todos os dias de manhã, por email e numa página reservada: o risco previsto para o concelho à escala de 1 km, a anomalia face à época, a tendência a 24 h, a classe oficial do IPMA lado a lado e os fogos dos últimos sete dias.',
    duty: 'Acompanhar e divulgar o risco de incêndio rural (competência do GTF).',
    to: '/ignis',
  },
  {
    name: 'Priorização de faixas de gestão de combustível (LIMES)',
    status: 'pilot',
    who: 'GTF',
    what: 'Lista ordenada de situações a verificar na rede secundária, ficha de evidência por parcela, sinal semanal de mudança e exportação para o registo anual.',
    duty: 'Monitorização da rede secundária e registo cartográfico anual das ações (art. 35.º e 49.º do DL 82/2021).',
    to: '/limes',
  },
  {
    name: 'Consola municipal de risco',
    status: 'live',
    who: 'SMPC, comissão municipal',
    what: 'O território do concelho por freguesia: risco de hoje, histórico da época, fogos recentes e o contraste entre a classe oficial do dia e a leitura a 1 km. Nacional desde o primeiro dia, uma conta por entidade.',
    duty: 'Apoio ao secretariado técnico da comissão municipal de gestão integrada de fogos rurais (art. 29.º).',
  },
  {
    name: 'Pacotes de dados para o programa municipal de execução',
    status: 'planned',
    who: 'Município ou CIM',
    what: 'Retrospetivas da época, mapas de intervenção priorizados e evidência de sítios e calendários para a revisão anual do PME e para a transição dos PMDFCI, cujo prazo foi estendido a 31 de dezembro de 2026.',
    duty: 'Programa municipal de execução, revisto anualmente, com rubrica orçamental (art. 35.º).',
  },
  {
    name: 'Estudos de evacuação e Aldeia Segura',
    status: 'planned',
    who: 'Proteção civil municipal',
    what: 'Cenários do simulador de propagação por aglomerado, para testar abrigos e vias de evacuação. O simulador é reservado a entidades oficiais e não é vendido como previsão horária de frentes de fogo.',
    duty: 'Planeamento de evacuação, inventário de abrigos e vias, Aldeia Segura e Pessoas Seguras (art. 17.º).',
  },
  {
    name: 'Licença intermunicipal (CIM)',
    status: 'planned',
    who: 'CIM e GTFI',
    what: 'Acesso a todos os concelhos membros numa só consola, com vistas sub-regionais para o planeamento que os gabinetes intermunicipais já centralizam.',
    duty: 'Planeamento sub-regional dos GTFI.',
  },
];

const STATUS: Record<Offer['status'], string> = {
  live: 'Em produção',
  pilot: 'Em piloto',
  planned: 'Em preparação',
};

export default function Municipios() {
  useDocumentMeta(
    'Tutela para municípios: risco de incêndio e gestão de combustível | Tutela',
    'Serviços da Tutela para câmaras municipais e CIM sob o DL 82/2021: briefing diário de risco por concelho, priorização de faixas de gestão de combustível (LIMES), consola municipal, pacotes de dados para o PME. Complemento ao IPMA e ao ICNF.',
    '/municipios',
  );

  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Tutela', item: `${SITE}/` },
      { '@type': 'ListItem', position: 2, name: 'Municípios', item: `${SITE}/municipios` },
    ],
  };

  // Service markup for the offerings that exist today (live or in pilot). Planned
  // ones stay out so the markup never implies availability. No prices by design.
  const services = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: OFFERS.filter((o) => o.status !== 'planned').map((o, i) => ({
      '@type': 'Service',
      position: i + 1,
      name: o.name,
      description: o.what,
      provider: { '@type': 'Organization', name: 'Tutela', url: SITE },
      areaServed: { '@type': 'Country', name: 'Portugal' },
      audience: { '@type': 'Audience', audienceType: o.who },
      ...(o.to ? { url: `${SITE}${o.to}` } : {}),
    })),
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-ink-950 text-mist">
      <JsonLd id="municipios-breadcrumb" data={breadcrumb} />
      <JsonLd id="municipios-services" data={services} />
      <div className="mx-auto max-w-3xl px-6 py-20">
        <div className="mb-6 font-mono text-xs uppercase tracking-[0.28em] text-ember">Municípios</div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-mist sm:text-4xl">
          O índice oficial diz o dia. Nós dizemos o sítio.
        </h1>
        <p className="mt-6 text-lg leading-relaxed text-mist-muted">
          A Tutela trabalha com gabinetes técnicos florestais e serviços municipais de proteção civil
          para cumprir os deveres do decreto-lei 82/2021 com dados: risco de incêndio à escala de 1 km
          dentro do concelho, e a rede secundária de faixas de combustível lida parcela a parcela. A
          classificação oficial de perigo continua a ser do IPMA e do ICNF; nós subdividimos o
          território, nunca contradizemos a classe.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <a
            href="mailto:hello@tutela.land?subject=Tutela%20para%20o%20nosso%20munic%C3%ADpio"
            className="inline-flex items-center gap-2 rounded-lg bg-ember px-6 py-3 font-semibold text-ink-950 shadow-[0_0_30px_-8px_#F26A4B] transition-all duration-200 hover:brightness-110"
          >
            Falar connosco
          </a>
          <Link
            to="/pilotos"
            className="inline-flex items-center gap-2 rounded-lg border border-ink-700 px-6 py-3 font-medium text-mist transition-colors hover:bg-ink-800"
          >
            Ver os pilotos
          </Link>
        </div>

        <div className="mt-16 border-t border-ink-800 pt-12">
          <div className="mb-2 font-mono text-xs uppercase tracking-[0.22em] text-ember">Serviços</div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-mist sm:text-3xl">
            Seis serviços, um dever legal cada
          </h2>
          <div className="mt-8 space-y-4">
            {OFFERS.map((o) => (
              <div key={o.name} className="rounded-xl border border-ink-700 bg-ink-900 p-6">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="font-display text-xl font-bold text-mist">{o.name}</h3>
                  <span
                    className={`rounded-full border px-3 py-1 font-mono text-[11px] uppercase tracking-wider ${
                      o.status === 'live'
                        ? 'border-ember/40 bg-ember/10 text-ember'
                        : o.status === 'pilot'
                          ? 'border-mist/20 bg-ink-800 text-mist'
                          : 'border-ink-700 text-mist-faint'
                    }`}
                  >
                    {STATUS[o.status]}
                  </span>
                </div>
                <p className="mt-3 text-mist-muted">{o.what}</p>
                <dl className="mt-4 grid gap-2 text-sm sm:grid-cols-[auto_1fr] sm:gap-x-4">
                  <dt className="font-mono text-[11px] uppercase tracking-wider text-mist-faint">Para quem</dt>
                  <dd className="text-mist-muted">{o.who}</dd>
                  <dt className="font-mono text-[11px] uppercase tracking-wider text-mist-faint">Dever</dt>
                  <dd className="text-mist-muted">{o.duty}</dd>
                </dl>
                {o.to && (
                  <Link to={o.to} className="mt-4 inline-block text-sm font-medium text-ember hover:brightness-110">
                    Saber mais
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16 border-t border-ink-800 pt-12">
          <div className="mb-2 font-mono text-xs uppercase tracking-[0.22em] text-ember">
            Como funciona o acesso
          </div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-mist sm:text-3xl">
            Público para todos, operacional após início de sessão
          </h2>
          <div className="mt-6 space-y-4 leading-relaxed text-mist-muted">
            <p>
              O mapa nacional, as páginas de risco por concelho e a validação pública são de acesso
              livre e assim ficam. Os serviços para municípios funcionam com contas por entidade: cada
              conta vê apenas o seu território e apenas os módulos contratados. As funcionalidades
              (briefing, consola, lista de faixas, ficha, exportação) existem só depois do início de
              sessão.
            </p>
            <p>
              Trabalhamos por pilotos curtos sobre o território real, com dados abertos e sem promessas
              que não conseguimos medir. A honestidade sobre o que os modelos fazem e não fazem, e a
              validação pública dia a dia contra os fogos reais, são a base de qualquer contrato.
            </p>
            <p>
              Financiamento: parte destes serviços cabe nos programas que já pagam capacidade técnica
              intermunicipal, como os apoios do Fundo Ambiental aos gabinetes técnicos florestais
              intermunicipais. Ajudamos a enquadrar o pedido.
            </p>
          </div>
        </div>

        <div className="mt-16 flex flex-wrap gap-4 border-t border-ink-800 pt-8">
          <a
            href="mailto:hello@tutela.land?subject=Tutela%20para%20o%20nosso%20munic%C3%ADpio"
            className="inline-flex items-center gap-2 rounded-lg bg-ember px-6 py-3 font-semibold text-ink-950 shadow-[0_0_30px_-8px_#F26A4B] transition-all duration-200 hover:brightness-110"
          >
            Falar connosco
          </a>
          <Link
            to="/ignis"
            className="inline-flex items-center gap-2 rounded-lg border border-ink-700 px-6 py-3 font-medium text-mist transition-colors hover:bg-ink-800"
          >
            <IgnisIcon className="h-4 w-4" />
            O IGNIS
          </Link>
          <Link
            to="/limes"
            className="inline-flex items-center gap-2 rounded-lg border border-ink-700 px-6 py-3 font-medium text-mist transition-colors hover:bg-ink-800"
          >
            O LIMES
          </Link>
        </div>
      </div>
    </div>
  );
}
